import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Backdrop,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Snackbar,
    Stack,
    Switch,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { MainStore } from '../../stores/MainStore';
import { Folder } from '../../models/Folder';
import { OrganizationSelection } from '../wizard/OrganizationSelection';
import { FileSlot } from './FileSlot';
import { ColumnDefinitions } from './ColumnDefinitions';
import { RulesToggleField } from './RulesToggleField';
import { RunsTab } from './RunsTab';
import {
    CatalogMode,
    FormState,
    PartIdAsSolutionName,
    RunMode,
    SlotKey,
    StartStage,
} from './formTypes';
import {
    ADVANCED_OPTION_HELP,
    AdvancedOptionKey,
    CLUSTERING_SLOT_KEYS,
    DEFAULT_MAX_CASES,
    MATCH_EXISTING_ENABLED,
    RESUME_ENABLED,
    SLOT_COLUMN_HELP,
    SLOT_KEYS,
    SLOT_LABELS,
    START_STAGES,
    initialForm,
} from './constants';
import { HelpPopoverButton } from './HelpPopoverButton';
import { isSubmittable, partColumnsValid, slotStates } from './formRules';
import { runCatalogGeneration } from './api';

type CatalogGenerationProps = { mainStore: MainStore };

// The boolean-valued keys of FormState — the fields the toggle handler can set.
type BooleanFormKey = {
    [K in keyof FormState]: FormState[K] extends boolean ? K : never;
}[keyof FormState];

const ModeCard = ({
    title,
    subtitle,
    selected,
    onClick,
    disabled = false,
}: {
    title: string;
    subtitle: string;
    selected: boolean;
    onClick: () => void;
    disabled?: boolean;
}) => (
    <Box
        onClick={disabled ? undefined : onClick}
        sx={{
            padding: 2,
            width: 240,
            minHeight: 120,
            border: '1px solid rgba(0,0,0,0.1)',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            backgroundColor: selected ? '#a7c3ff' : '#e9f0ff',
            '&:hover': { backgroundColor: disabled ? '#e9f0ff' : '#a7c3ff' },
        }}
    >
        <Typography variant="subtitle1">{title}</Typography>
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)' }}>{subtitle}</Typography>
    </Box>
);

export const CatalogGeneration = observer(({ mainStore }: CatalogGenerationProps) => {
    // Tenant list mirrors the Copilot "Customer" dropdown: the configured
    // tenants from /triageCustomers, not the /orgs storage folders.
    const tenantOptions: Folder[] = (mainStore.copilotStore?.customers ?? [])
        .slice()
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(tc => ({ name: tc.name, url: tc.id }));
    const [form, setForm] = useState<FormState>(initialForm);
    const [tab, setTab] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState<{ text: string; severity: 'success' | 'error' } | null>(null);
    const [previousRuns, setPreviousRuns] = useState<{ run_id: string; run_ts?: string; status?: string }[]>([]);
    const [uploadProgress, setUploadProgress] = useState<{ uploaded: number; total: number } | null>(null);

    const states = slotStates(form);
    const historicalColumns = form.fileColumns.historical_cases ?? [];

    // Files render in two places now — the Files section and, for the clustering
    // slots, inside Advanced under their own toggle. One renderer, one set of rules.
    const renderSlot = (k: SlotKey) => {
        const state = states[k];
        if (state === 'hidden') return null;
        const file = form.files[k];
        return (
            <FileSlot
                key={k}
                label={SLOT_LABELS[k]}
                required={state === 'required'}
                disabled={state === 'disabled'}
                invalid={state === 'required' && file === null}
                hideColumns
                file={file}
                helpColumns={SLOT_COLUMN_HELP[k]}
                onChange={s => setFile(k)(s.file, s.allColumns)}
                onRemove={file ? removeFile(k) : undefined}
            />
        );
    };

    // Plain-text explanation popover for an Advanced-section option, keyed into
    // ADVANCED_OPTION_HELP. Falls back to a placeholder until real copy is authored.
    const renderAdvancedHelp = (key: AdvancedOptionKey, ariaLabel: string) => (
        <HelpPopoverButton
            ariaLabel={ariaLabel}
            renderContent={() => (
                <Typography variant="body2" sx={{ maxWidth: 320, whiteSpace: 'pre-line' }}>
                    {ADVANCED_OPTION_HELP[key] || 'No description yet.'}
                </Typography>
            )}
        />
    );

    // An Advanced clustering toggle plus the codes file it consumes, revealed only
    // while the toggle is on. Shared so the alignment workaround, the indent, and the
    // required-file presentation cannot drift between the error-code and
    // solution-code rows. `setFlag` is declared below — fine, since this body only
    // runs when the JSX is evaluated in the return.
    const renderClusteringToggle = (
        flag: BooleanFormKey,
        label: string,
        slot: SlotKey,
        helpKey: AdvancedOptionKey,
        disabled = false,
    ) => (
        <Box key={slot}>
            <Stack direction="row" alignItems="center">
                <FormControlLabel
                    // Stack only resets a direct child's default margin when it has a truthy
                    // `spacing` prop (see createStack.js); this row has none, so zero it
                    // explicitly to match every other row's switch position.
                    sx={{ ml: 0 }}
                    control={<Switch checked={form[flag]} onChange={setFlag(flag)} disabled={disabled} />}
                    label={label}
                />
                {renderAdvancedHelp(helpKey, `${label} help`)}
            </Stack>
            {form[flag] && <Box sx={{ ml: 4, mt: 1 }}>{renderSlot(slot)}</Box>}
        </Box>
    );

    useEffect(() => {
        setForm(prev => {
            let changed = false;
            const nextFiles = { ...prev.files };
            const nextColumns = { ...prev.fileColumns };
            const s = slotStates(prev);
            for (const k of SLOT_KEYS) {
                if ((s[k] === 'disabled' || s[k] === 'hidden') && (nextFiles[k] !== null || nextColumns[k] !== undefined)) {
                    nextFiles[k] = null;
                    nextColumns[k] = undefined;
                    changed = true;
                }
            }
            // 'Cluster diagnostics by components' is subordinate to Extract diagnostics.
            // Unchecking that has to switch the toggle off outright, not leave it armed to
            // reappear required-and-empty the moment diagnostics is re-checked. Normalized
            // here rather than in setFlag because this effect is already the single place
            // extractDiagnostics' cascading consequences are handled — the slot's file is
            // cleared by the loop above in the same commit.
            const clusterByComponents = prev.extractDiagnostics && prev.clusterDiagnosticsByComponents;
            if (clusterByComponents !== prev.clusterDiagnosticsByComponents) changed = true;
            return changed
                ? {
                    ...prev,
                    files: nextFiles,
                    fileColumns: nextColumns,
                    clusterDiagnosticsByComponents: clusterByComponents,
                }
                : prev;
        });
    }, [
        form.runMode,
        form.catalogMode,
        form.extractObservations,
        form.extractSolutions,
        form.extractDiagnostics,
        form.useExactMatchErrorCodeClustering,
        form.useExactMatchSolutionCodeClustering,
        form.clusterDiagnosticsByComponents,
    ]);

    // Every boolean toggle on this form must capture `checked` EAGERLY, before calling
    // setForm. Three of them — extractDiagnostics and the two exact-match clustering
    // flags — are dependencies of the clear-slot effect above, so flipping one can
    // commit a second state update in the same interaction. React then restores the
    // controlled input's DOM value (react-dom's restoreStateIfNeeded), so a lazy
    // `e.target.checked` read from inside the updater observes the pre-click value and
    // the toggle silently no-ops: switching a clustering toggle off (which clears its
    // file) and back on failed to re-reveal the slot, and Extract diagnostics would not
    // turn back on once its file had been cleared. The other three toggles cannot
    // cascade that way, but they route through here too so the pattern stays uniform.
    const setFlag = (key: BooleanFormKey) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setForm(p => ({ ...p, [key]: checked }));
    };

    const setRunMode = (m: RunMode) => setForm(p => ({ ...p, runMode: m }));
    const setCatalogMode = (m: CatalogMode) => setForm(p => ({ ...p, catalogMode: m }));
    const setFile = (k: SlotKey) => (file: File | null, columns: string[]) =>
        setForm(p => {
            // FileSlot parses headers asynchronously and only calls back afterwards, so a
            // file picked just before its slot was hidden or disabled resolves once the
            // slot is already closed. Drop those writes: the clear-slot effect cannot
            // undo them (`files` is not one of its dependencies, so it never re-runs) and
            // runCatalogGeneration uploads every non-null file regardless of slot state,
            // so the file would reach the run — turning component clustering on with its
            // toggle switched off, for instance.
            const state = slotStates(p)[k];
            if (state === 'hidden' || state === 'disabled') return p;
            return {
                ...p,
                files: { ...p.files, [k]: file },
                fileColumns: { ...p.fileColumns, [k]: columns },
            };
        });

    // Removal resets fileColumns to undefined, not [] — partColumnsBlocking reads a
    // defined entry as "a candidate file exists", so an empty array left behind would
    // raise the part-columns blocker over a slot that no longer holds anything.
    const removeFile = (k: SlotKey) => () =>
        setForm(p => ({
            ...p,
            files: { ...p.files, [k]: null },
            fileColumns: { ...p.fileColumns, [k]: undefined },
        }));

    // A run id only means anything for the tenant it was listed under, so changing
    // tenant has to drop the selection too — otherwise resume mode would keep the old
    // tenant's run selected and submit it against the new one.
    const handleTenant = (org?: string) =>
        setForm(p => ({ ...p, tenantId: org ?? null, previousRun: null }));

    useEffect(() => {
        // Clear whatever the previous tenant loaded, so its runs can't stay on screen
        // while the new tenant's list is in flight (or after the tenant is cleared).
        setPreviousRuns(prev => (prev.length ? [] : prev));
        if (!form.tenantId) return;
        const tenantId = form.tenantId;
        // Switching tenant A -> B leaves A's request running; without this guard A's
        // slower response would overwrite B's list, or its .catch would blank it.
        let currentRequest = true;
        mainStore.mainApi!.fetchCatalogRuns(tenantId)
            .then(resp => { if (currentRequest) setPreviousRuns(resp.runs ?? []); })
            .catch(() => { if (currentRequest) setPreviousRuns([]); });
        return () => { currentRequest = false; };
    }, [form.tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        setForm(prev => {
            // Prune only against a present file's headers. When the file is removed
            // (or a replacement fails to parse) the mappings must survive untouched,
            // ready for the next upload — whose headers will prune them here anyway.
            if (!prev.files.historical_cases) return prev;
            const colSet = new Set(historicalColumns);
            let changed = false;
            const next = prev.columnDefinitions.map(row => {
                const filtered = row.columns.filter(c => colSet.has(c));
                if (filtered.length !== row.columns.length) {
                    changed = true;
                    return { ...row, columns: filtered };
                }
                return row;
            });
            return changed ? { ...prev, columnDefinitions: next } : prev;
        });
    }, [form.fileColumns.historical_cases]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const result = await runCatalogGeneration(
                mainStore.mainApi!,
                form,
                mainStore.user?.profile.email,
                (uploaded, total) => setUploadProgress({ uploaded, total }),
            );
            if (result.success) {
                setFeedbackMessage({ text: `Generation started${result.run_id ? ` (run: ${result.run_id})` : ''}.`, severity: 'success' });
                setForm(initialForm);
                setPreviousRuns([]);
            } else {
                setFeedbackMessage({ text: result.description ?? 'Submission failed.', severity: 'error' });
            }
        } catch (err: any) {
            setFeedbackMessage({ text: err?.message ?? 'An error occurred.', severity: 'error' });
        } finally {
            setSubmitting(false);
            setUploadProgress(null);
        }
    };

    const submittable = isSubmittable(form);
    // Surface the part-column requirement only once a candidate file is
    // uploaded, so the message explains a concrete submission block.
    const partColumnsBlocking =
        form.runMode === 'new' &&
        (form.fileColumns.historical_cases !== undefined || form.fileColumns.part_info !== undefined) &&
        !partColumnsValid(form);
    // The clustering slots live inside the collapsible Advanced accordion. MUI
    // keeps a collapsed panel's content mounted but invisible, so a
    // required-and-empty slot in there blocks Submit with no visible reason
    // once Advanced is collapsed. Surface it here, the same way partColumnsBlocking does.
    const missingAdvancedSlots = CLUSTERING_SLOT_KEYS.filter(
        k => states[k] === 'required' && form.files[k] === null,
    );

    return (
        <Stack style={{ height: 'calc(100vh - 70px)', padding: '24px', overflow: 'auto' }} spacing={2}>
            <Typography variant="h5">Catalog Generation</Typography>

            <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                aria-label="Catalog generation sections"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
                <Tab label="Generate" id="catalog-tab-0" aria-controls="catalog-tabpanel-0" />
                <Tab label="Runs" id="catalog-tab-1" aria-controls="catalog-tabpanel-1" />
            </Tabs>

            {tab === 0 && (
            <Paper
                id="catalog-tabpanel-0"
                role="tabpanel"
                aria-labelledby="catalog-tab-0"
                elevation={1}
                sx={{ padding: 3 }}
            >
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Tenant</Typography>
                        <OrganizationSelection
                            organizations={tenantOptions}
                            onSelection={handleTenant}
                            selectedOrg={form.tenantId ?? undefined}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Run mode</Typography>
                        <Stack direction="row" spacing={2}>
                            <ModeCard
                                title="New"
                                subtitle="Start a fresh catalog generation run."
                                selected={form.runMode === 'new'}
                                onClick={() => setRunMode('new')}
                            />
                            <ModeCard
                                title="Resume"
                                subtitle={RESUME_ENABLED
                                    ? 'Continue a previous run from a stage.'
                                    : 'Continue a previous run from a stage. (Coming soon)'}
                                selected={form.runMode === 'resume'}
                                onClick={() => setRunMode('resume')}
                                disabled={!RESUME_ENABLED}
                            />
                        </Stack>
                    </Box>

                    {form.runMode === 'resume' && (
                        <Stack direction="row" spacing={2}>
                            <FormControl sx={{ minWidth: 260 }}>
                                <InputLabel id="prev-run-label">Previous run</InputLabel>
                                <Select
                                    labelId="prev-run-label"
                                    label="Previous run"
                                    value={form.previousRun ?? ''}
                                    onChange={e => setForm(p => ({ ...p, previousRun: e.target.value as string }))}
                                >
                                    {previousRuns.map(run => (
                                        <MenuItem key={run.run_id} value={run.run_id}>{run.run_ts ?? run.run_id}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl sx={{ minWidth: 260 }}>
                                <InputLabel id="start-stage-label">Start stage</InputLabel>
                                <Select
                                    labelId="start-stage-label"
                                    label="Start stage"
                                    value={form.startStage ?? ''}
                                    onChange={e => setForm(p => ({ ...p, startStage: e.target.value as StartStage }))}
                                >
                                    {START_STAGES.map(s => (
                                        <MenuItem key={s} value={s}>{s}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Stack>
                    )}

                    {form.runMode === 'new' && (
                        <>
                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Extraction</Typography>
                                <Stack direction="row" spacing={3}>
                                    <FormControlLabel
                                        control={<Checkbox checked={form.extractObservations} disabled />}
                                        label="Extract observations"
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={form.extractSolutions} disabled />}
                                        label="Extract solutions"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={form.extractDiagnostics}
                                                onChange={setFlag('extractDiagnostics')}
                                            />
                                        }
                                        label="Extract diagnostics"
                                    />
                                </Stack>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Catalog mode</Typography>
                                <Stack direction="row" spacing={2}>
                                    {MATCH_EXISTING_ENABLED && (
                                        <ModeCard
                                            title="Match existing"
                                            subtitle="Match against an existing catalog."
                                            selected={form.catalogMode === 'match_existing'}
                                            onClick={() => setCatalogMode('match_existing')}
                                        />
                                    )}
                                    <ModeCard
                                        title="Generate fresh"
                                        subtitle="Build a brand-new catalog from scratch."
                                        selected={form.catalogMode === 'generate_fresh'}
                                        onClick={() => setCatalogMode('generate_fresh')}
                                        // Sole remaining mode while match-existing is off: shown
                                        // chosen and locked, like the extraction checkboxes.
                                        disabled={!MATCH_EXISTING_ENABLED}
                                    />
                                </Stack>
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Files</Typography>
                                <Stack spacing={2}>
                                    {SLOT_KEYS
                                        .filter(k => !CLUSTERING_SLOT_KEYS.includes(k))
                                        .map(renderSlot)}
                                </Stack>
                            </Box>

                            {form.files.historical_cases && (
                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'rgba(0,0,0,0.12)',
                                        borderRadius: 2,
                                        padding: 2,
                                        backgroundColor: '#fafbff',
                                    }}
                                >
                                    <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                                        Free text column definitions
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', mb: 2 }}>
                                        Define the meaning of free-text columns from the uploaded historical-cases file.
                                    </Typography>
                                    <ColumnDefinitions
                                        rows={form.columnDefinitions}
                                        columns={historicalColumns}
                                        onChange={rows =>
                                            setForm(p => ({ ...p, columnDefinitions: rows }))
                                        }
                                    />
                                </Box>
                            )}

                            <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="subtitle2">Advanced</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={1}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <TextField
                                                label="Product Type (optional)"
                                                value={form.productType}
                                                onChange={e => setForm(p => ({ ...p, productType: e.target.value }))}
                                                sx={{ flex: 1 }}
                                            />
                                            {renderAdvancedHelp('productType', 'Product Type help')}
                                        </Stack>
                                        <Stack direction="row" alignItems="center">
                                            <FormControlLabel
                                                // Stack only resets a direct child's default margin when it has a
                                                // truthy `spacing` prop (see createStack.js); this row has none, so
                                                // zero it explicitly to match every other row's switch position.
                                                sx={{ ml: 0 }}
                                                control={
                                                    <Switch
                                                        checked={form.maxCasesEnabled}
                                                        onChange={setFlag('maxCasesEnabled')}
                                                    />
                                                }
                                                label="Limit max cases"
                                            />
                                            {renderAdvancedHelp('maxCasesEnabled', 'Limit max cases help')}
                                        </Stack>
                                        <TextField
                                            type="number"
                                            label="Max cases"
                                            value={form.maxCases}
                                            disabled={!form.maxCasesEnabled}
                                            onChange={e => {
                                                const n = Number(e.target.value);
                                                setForm(p => ({ ...p, maxCases: Number.isFinite(n) ? n : DEFAULT_MAX_CASES }));
                                            }}
                                            inputProps={{ min: 1 }}
                                        />
                                        <Stack direction="row" alignItems="center">
                                            <FormControlLabel
                                                sx={{ ml: 0 }}
                                                control={
                                                    <Switch
                                                        checked={form.useCustomInstructions}
                                                        onChange={setFlag('useCustomInstructions')}
                                                    />
                                                }
                                                label="Use custom instructions"
                                            />
                                            {renderAdvancedHelp('useCustomInstructions', 'Use custom instructions help')}
                                        </Stack>
                                        <RulesToggleField
                                            label="Non-semantic solution-code clustering"
                                            checked={form.useSolutionCodeNonSemanticClustering}
                                            onToggle={v => setForm(p => ({ ...p, useSolutionCodeNonSemanticClustering: v }))}
                                            value={form.solutionCodeNonSemanticClusteringRules}
                                            onChange={v => setForm(p => ({ ...p, solutionCodeNonSemanticClusteringRules: v }))}
                                            helpButton={renderAdvancedHelp('useSolutionCodeNonSemanticClustering', 'Non-semantic solution-code clustering help')}
                                        />
                                        <Stack direction="row" alignItems="center">
                                            <FormControlLabel
                                                sx={{ ml: 0 }}
                                                control={
                                                    <Switch
                                                        checked={form.ruleBasedObsLabelMerge}
                                                        onChange={setFlag('ruleBasedObsLabelMerge')}
                                                    />
                                                }
                                                label="Rule-based observation label merge"
                                            />
                                            {renderAdvancedHelp('ruleBasedObsLabelMerge', 'Rule-based observation label merge help')}
                                        </Stack>
                                        {renderClusteringToggle(
                                            'useExactMatchErrorCodeClustering',
                                            'Exact-match error-code clustering',
                                            'error_codes',
                                            'useExactMatchErrorCodeClustering',
                                        )}
                                        {renderClusteringToggle(
                                            'useExactMatchSolutionCodeClustering',
                                            'Exact-match solution-code clustering',
                                            'solution_codes_filename',
                                            'useExactMatchSolutionCodeClustering',
                                        )}
                                        {renderClusteringToggle(
                                            'clusterDiagnosticsByComponents',
                                            'Cluster diagnostics by components',
                                            'diagnostic_component',
                                            'clusterDiagnosticsByComponents',
                                            !form.extractDiagnostics,
                                        )}
                                        <RulesToggleField
                                            label="Custom post processing"
                                            checked={form.useCustomPostProcessing}
                                            onToggle={v => setForm(p => ({ ...p, useCustomPostProcessing: v }))}
                                            value={form.customPostProcessingRules}
                                            onChange={v => setForm(p => ({ ...p, customPostProcessingRules: v }))}
                                            helpButton={renderAdvancedHelp('useCustomPostProcessing', 'Custom post processing help')}
                                        />
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <FormControl sx={{ minWidth: 260 }}>
                                                <InputLabel id="part-id-as-solution-label">Part id as solution name</InputLabel>
                                                <Select
                                                    labelId="part-id-as-solution-label"
                                                    label="Part id as solution name"
                                                    value={form.partIdAsSolutionName}
                                                    onChange={e => setForm(p => ({ ...p, partIdAsSolutionName: e.target.value as PartIdAsSolutionName }))}
                                                >
                                                    <MenuItem value="off">Off</MenuItem>
                                                    <MenuItem value="duplicate">Duplicate</MenuItem>
                                                    <MenuItem value="replace">Replace</MenuItem>
                                                </Select>
                                            </FormControl>
                                            {renderAdvancedHelp('partIdAsSolutionName', 'Part id as solution name help')}
                                        </Stack>
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        </>
                    )}

                    {partColumnsBlocking && (
                        <Alert severity="warning">
                            Cannot submit: one file — Historical cases or Part replacement history — must
                            contain both a <code>part_id</code> and a <code>part_description</code> column.
                        </Alert>
                    )}

                    {missingAdvancedSlots.length > 0 && (
                        <Alert severity="warning">
                            {`Cannot submit: the ${missingAdvancedSlots.map(k => SLOT_LABELS[k]).join(' and ')} ` +
                                `${missingAdvancedSlots.length > 1 ? 'files are' : 'file is'} required. ` +
                                `Open Advanced and upload ${missingAdvancedSlots.length > 1 ? 'them' : 'it'}.`}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            disabled={!submittable || submitting}
                            onClick={handleSubmit}
                        >
                            {submitting ? 'Submitting…' : 'Submit'}
                        </Button>
                    </Box>
                </Stack>
            </Paper>
            )}

            {tab === 1 && (
                <Box id="catalog-tabpanel-1" role="tabpanel" aria-labelledby="catalog-tab-1">
                    {mainStore.mainApi
                        ? <RunsTab mainApi={mainStore.mainApi} />
                        : <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>}
                </Box>
            )}

            <Snackbar
                open={feedbackMessage !== null}
                autoHideDuration={6000}
                onClose={() => setFeedbackMessage(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity={feedbackMessage?.severity ?? 'info'}
                    onClose={() => setFeedbackMessage(null)}
                    sx={{ width: '100%' }}
                >
                    {feedbackMessage?.text}
                </Alert>
            </Snackbar>

            <Backdrop
                open={submitting}
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1, flexDirection: 'column', gap: 2 }}
            >
                {uploadProgress && uploadProgress.total > 0 && uploadProgress.uploaded < uploadProgress.total ? (
                    <>
                        <CircularProgress color="inherit" variant="determinate" value={(uploadProgress.uploaded / uploadProgress.total) * 100} />
                        <Typography>{`Uploading files… (${uploadProgress.uploaded}/${uploadProgress.total})`}</Typography>
                    </>
                ) : (
                    <>
                        <CircularProgress color="inherit" />
                        <Typography>Starting run…</Typography>
                    </>
                )}
            </Backdrop>
        </Stack>
    );
});
